// src/features/payment/BankRequisitesModal.tsx
// Edits bank account requisites on a payment method (UpdateBankRequisites) and
// links/unlinks the method to a LegalEntity for contract rendering
// (AttachPaymentMethodToLegalEntity / DetachPaymentMethodFromLegalEntity).
//
// One dialog, two logical sections: the legal-entity link is a single-field,
// immediate-effect settings row (autosave on button click, not part of the
// dialog's primary submit); the bank requisites block — only shown for
// bank-account methods — is the actual "form", wired to the dialog's ⌘⏎/primary
// submit.

import { useEffect, useRef, useState } from 'react';
import { useI18n } from '@react/hooks';
import { Button } from '@react/components';
import { FormDialog, FormRow } from '@/components/form';
import type { PaymentMethodDetail } from '@core/types/payment';
import { errMessage } from './format';

interface RequisitesForm {
  [key: string]: string;
  account_holder_name: string;
  account_number: string;
  iban: string;
  swift_bic: string;
  country_code: string;
  correspondent_account: string;
  bank_name: string;
  bank_address: string;
  currency: string;
}

const EMPTY_FORM: RequisitesForm = {
  account_holder_name: '',
  account_number: '',
  iban: '',
  swift_bic: '',
  country_code: '',
  correspondent_account: '',
  bank_name: '',
  bank_address: '',
  currency: '',
};

export interface BankRequisitesModalProps {
  open: boolean;
  method: PaymentMethodDetail | null;
  isSaving?: boolean;
  onCancel: () => void;
  onSaveRequisites: (bankRequisites: Record<string, unknown>) => Promise<void>;
  onAttachLegalEntity: (legalEntityId: string) => Promise<void>;
  onDetachLegalEntity: () => Promise<void>;
}

export function BankRequisitesModal({
  open,
  method,
  isSaving,
  onCancel,
  onSaveRequisites,
  onAttachLegalEntity,
  onDetachLegalEntity,
}: BankRequisitesModalProps) {
  const { t } = useI18n();
  const [form, setForm] = useState<RequisitesForm>(EMPTY_FORM);
  const initialFormRef = useRef<RequisitesForm>(EMPTY_FORM);
  const [legalEntityId, setLegalEntityId] = useState('');
  const initialLegalEntityRef = useRef('');
  const [error, setError] = useState<string | null>(null);
  const [legalEntityBusy, setLegalEntityBusy] = useState(false);

  useEffect(() => {
    const r = method?.bank_requisites;
    const next: RequisitesForm = {
      account_holder_name: r?.account_holder_name ?? '',
      account_number: r?.account_number ?? '',
      iban: r?.iban ?? '',
      swift_bic: r?.swift_bic ?? '',
      country_code: r?.country_code ?? '',
      correspondent_account: r?.correspondent_account ?? '',
      bank_name: r?.bank_name ?? '',
      bank_address: r?.bank_address ?? '',
      currency: r?.currency ?? '',
    };
    setForm(next);
    initialFormRef.current = next;
    const legalId = method?.legal_entity?.id ?? '';
    setLegalEntityId(legalId);
    initialLegalEntityRef.current = legalId;
    setError(null);
  }, [method]);

  if (!method) return null;

  const change = (field: keyof RequisitesForm, value: string) => setForm((p) => ({ ...p, [field]: value }));

  const isBankAccount = method.type === 'PAYMENT_METHOD_TYPE_BANK_ACCOUNT';

  const saveRequisites = async () => {
    setError(null);
    try {
      await onSaveRequisites(form);
    } catch (err) {
      setError(errMessage(err, t('payment.methods.requisitesSaveError')));
    }
  };

  const handleLegalEntity = async () => {
    setError(null);
    setLegalEntityBusy(true);
    try {
      if (legalEntityId.trim()) {
        await onAttachLegalEntity(legalEntityId.trim());
      } else {
        await onDetachLegalEntity();
      }
      initialLegalEntityRef.current = legalEntityId.trim();
    } catch (err) {
      setError(errMessage(err, t('payment.methods.legalEntityError')));
    } finally {
      setLegalEntityBusy(false);
    }
  };

  const requisitesDirty = isBankAccount && JSON.stringify(form) !== JSON.stringify(initialFormRef.current);
  const legalEntityDirty = legalEntityId.trim() !== initialLegalEntityRef.current;
  const dirty = requisitesDirty || legalEntityDirty;

  return (
    <FormDialog
      open={open}
      onClose={onCancel}
      onSubmit={isBankAccount ? saveRequisites : onCancel}
      submitLabel={isBankAccount ? t('common.saveChanges') : t('common.close')}
      canSubmit
      isSubmitting={isSaving}
      dirty={dirty}
      width="lg"
      aria-label={t('payment.methods.manage')}
    >
      <div className="space-y-4 px-3.5 pb-4 pt-3">
        {error && <p className="text-sm text-destructive">{error}</p>}

        <div className="space-y-2">
          <FormRow label={t('payment.methods.legalEntity')} labelWidth="sm" hint={t('payment.methods.legalEntityHint')}>
            <div className="flex gap-2">
              <input
                data-autofocus
                value={legalEntityId}
                onChange={(e) => setLegalEntityId(e.target.value)}
                placeholder={t('payment.methods.legalEntityIdPlaceholder')}
                autoCapitalize="off"
                className="flex-1 rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
              />
              <Button type="button" variant="secondary" size="sm" onClick={handleLegalEntity} isLoading={legalEntityBusy}>
                {legalEntityId.trim() ? t('common.save') : t('common.remove')}
              </Button>
            </div>
          </FormRow>
        </div>

        {isBankAccount && (
          <div className="space-y-1 border-t border-border pt-3">
            <h4 className="mb-1 text-sm font-medium text-foreground">{t('payment.methods.bankRequisites')}</h4>
            <FormRow label={t('payment.methods.accountHolder')} labelWidth="sm">
              <input value={form.account_holder_name} onChange={(e) => change('account_holder_name', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('payment.methods.accountNumber')} labelWidth="sm">
              <input value={form.account_number} onChange={(e) => change('account_number', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label="IBAN" labelWidth="sm">
              <input value={form.iban} onChange={(e) => change('iban', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label="SWIFT/BIC" labelWidth="sm">
              <input value={form.swift_bic} onChange={(e) => change('swift_bic', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('payment.methods.countryCode')} labelWidth="sm">
              <input value={form.country_code} onChange={(e) => change('country_code', e.target.value.toUpperCase())} placeholder="US" autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('payment.methods.correspondentAccount')} labelWidth="sm">
              <input value={form.correspondent_account} onChange={(e) => change('correspondent_account', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('payment.methods.bankName')} labelWidth="sm">
              <input value={form.bank_name} onChange={(e) => change('bank_name', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('payment.methods.currency')} labelWidth="sm">
              <input value={form.currency} onChange={(e) => change('currency', e.target.value.toUpperCase())} placeholder="USD" autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 font-mono text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
            <FormRow label={t('payment.methods.bankAddress')} labelWidth="sm">
              <input value={form.bank_address} onChange={(e) => change('bank_address', e.target.value)} autoCapitalize="off" className="w-full rounded-md border border-input bg-background px-2.5 py-1 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-ring" />
            </FormRow>
          </div>
        )}
      </div>
    </FormDialog>
  );
}

export default BankRequisitesModal;
